import { NavLink, Outlet, useNavigate } from 'react-router-dom'
import {
  AppShell,
  Box,
  Button,
  Container,
  Group,
  Text,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { ColorSchemeToggle } from '@racedash/shared/ui'
import { SECTIONS } from '../lib/sections'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import StatusDot from '../components/StatusDot'
import { useNow, useOpsHealth } from '../data/ops'

/**
 * The admin shell.
 *
 * The nav stays react-router's NavLink rather than Mantine's own: routing is
 * the router's job, and Mantine's NavLink is a list-item control for sidebars
 * rather than a horizontal bar. Only the appearance comes from the kit.
 */
export default function Home() {
  const { state, signOutNow } = useAdminAuth()
  const user = state.status === 'admin' ? state.user : null

  return (
    <AppShell header={{ height: 56 }} padding={0}>
      <AppShell.Header>
        <Container size="lg" h="100%">
          <Group h="100%" justify="space-between" wrap="nowrap">
            <Group gap="lg" wrap="nowrap">
              <Text fw={700} size="sm" style={{ letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
                RaceDash <span className="tag">admin</span>
              </Text>
              <Group gap={2} component="nav" wrap="nowrap">
                {SECTIONS.map((s) => (
                  <NavLink
                    key={s.path}
                    to={s.path}
                    end={s.end}
                    className={({ isActive }) => (isActive ? 'navlink active' : 'navlink')}
                  >
                    {s.label}
                  </NavLink>
                ))}
              </Group>
            </Group>
            <Group gap="xs" wrap="nowrap">
              <FleetIndicator />
              <Text c="dimmed" size="xs" visibleFrom="sm">
                {user?.email}
              </Text>
              <ColorSchemeToggle />
              <Button variant="subtle" color="gray" onClick={() => void signOutNow()}>
                Sign out
              </Button>
            </Group>
          </Group>
        </Container>
      </AppShell.Header>

      <AppShell.Main>
        <Container size="lg" py="xl">
          <Box>
            <Outlet />
          </Box>
        </Container>
      </AppShell.Main>
    </AppShell>
  )
}

/**
 * The verdict, in the header, on every page.
 *
 * "Are there problems I need to be aware of?" is not really answered by a page
 * somebody has to remember to open. This is the same judgement the status page
 * renders, from the same hook, so the two cannot disagree — and it is a link,
 * because the only useful next move after noticing it is red is to go and look.
 */
function FleetIndicator() {
  const navigate = useNavigate()
  const now = useNow(30_000)
  const health = useOpsHealth(now)

  return (
    <Tooltip label={health.verdict} withArrow>
      <UnstyledButton
        onClick={() => navigate('/status')}
        aria-label={`Status: ${health.verdict}`}
        style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderRadius: 6 }}
      >
        <StatusDot tone={health.tone} size={9} />
      </UnstyledButton>
    </Tooltip>
  )
}
